export type ScrollAxis = 'x' | 'y';

const axisGeometry = {
  x: {
    overflow: 'overflowX',
    start: 'left',
    end: 'right',
    scroll: 'scrollLeft',
  },
  y: {
    overflow: 'overflowY',
    start: 'top',
    end: 'bottom',
    scroll: 'scrollTop',
  },
} as const;

const findScrollableAncestor = (
  element: HTMLElement,
  axis: ScrollAxis
): HTMLElement | null => {
  for (
    let ancestor = element.parentElement;
    ancestor;
    ancestor = ancestor.parentElement
  ) {
    const overflow = getComputedStyle(ancestor)[axisGeometry[axis].overflow];
    if (overflow === 'auto' || overflow === 'scroll') {
      return ancestor;
    }
  }
  return null;
};

/**
 * Scrolls the nearest ancestor that scrolls on the given axis just enough to
 * make the element visible. Does nothing if the element is already visible or
 * no scrollable ancestor exists.
 *
 * Prefer this over `scrollIntoView` when the element lives inside a nested
 * scroll container: `scrollIntoView` also scrolls outer containers along the
 * other axis, so it would pull the page vertically when only a horizontal
 * strip needs to move.
 */
export const scrollIntoViewWithinScrollParent = (
  element: HTMLElement,
  axis: ScrollAxis
): void => {
  const scrollParent = findScrollableAncestor(element, axis);
  if (scrollParent === null) {
    return;
  }
  const { start, end, scroll } = axisGeometry[axis];
  const parentBox = scrollParent.getBoundingClientRect();
  const elementBox = element.getBoundingClientRect();
  if (elementBox[start] < parentBox[start]) {
    scrollParent[scroll] += elementBox[start] - parentBox[start];
    return;
  }
  if (elementBox[end] > parentBox[end]) {
    scrollParent[scroll] += elementBox[end] - parentBox[end];
  }
};

const getVerticallyScrollableAncestors = (
  element: HTMLElement
): HTMLElement[] => {
  const ancestors: HTMLElement[] = [];
  for (
    let ancestor = element.parentElement;
    ancestor;
    ancestor = ancestor.parentElement
  ) {
    const overflowY = getComputedStyle(ancestor)[axisGeometry.y.overflow];
    if (overflowY === 'auto' || overflowY === 'scroll') {
      ancestors.push(ancestor);
    }
  }
  return ancestors;
};

const isDocumentScrollDisabled = (scrollingElement: Element): boolean => {
  const overflowY = getComputedStyle(scrollingElement)[axisGeometry.y.overflow];
  return overflowY === 'hidden' || overflowY === 'clip';
};

/**
 * Aligns the element to the top of each scrollable ancestor in turn, falling
 * back to the document. Skips `overflow: hidden` containers - never scrolls
 * something the user can't scroll back.
 */
export const scrollToTopOfScrollParent = (
  element: HTMLElement,
  behavior?: ScrollBehavior
): void => {
  const scrollableAncestors = getVerticallyScrollableAncestors(element);

  const outermostTop = scrollableAncestors.reduce((referenceTop, ancestor) => {
    const ancestorTop = ancestor.getBoundingClientRect().top;
    ancestor.scrollBy({ top: referenceTop - ancestorTop, behavior });
    return ancestorTop;
  }, element.getBoundingClientRect().top);

  if (scrollableAncestors.length > 0) {
    return;
  }

  const scrollingElement = document.scrollingElement;
  if (!scrollingElement || isDocumentScrollDisabled(scrollingElement)) {
    return;
  }
  scrollingElement.scrollBy({ top: outermostTop, behavior });
};
